import React from 'react';
import { Controller, FieldValues, useFormState } from 'react-hook-form';
import { BaseFormFieldProps } from './types';
import DepartmentSelector from '../selectors/DepartmentSelector';
import { useDepartments } from '@/hooks/queries/useDepartments';

export interface DepartmentSelectorComponentProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  isInvalid?: boolean;
  errorMessage?: string;
  isRequired?: boolean;
  label?: string;
  variant?: 'flat' | 'bordered' | 'faded' | 'underlined';
  className?: string;
  'aria-label'?: string;
  showEmptyOption?: boolean;
  isDisabled?: boolean;
}

export interface FormDepartmentSelectorProps<TFieldValues extends FieldValues = FieldValues>
  extends Omit<BaseFormFieldProps<TFieldValues>, 'label' | 'placeholder'> {
  placeholder?: string;
  label?: string;
  showEmptyOption?: boolean;
  isDisabled?: boolean;
}

const FormDepartmentSelector = <TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  isRequired = false,
  showErrorOnTouched = true,
  placeholder,
  label,
  showEmptyOption = true,
  isDisabled = false,
  ...rest
}: FormDepartmentSelectorProps<TFieldValues>) => {
  const { touchedFields, errors, isSubmitted } = useFormState({ control });
  const { data: departments = [], isLoading: isLoadingDepartments } = useDepartments();
  const fieldError = errors[name]?.message as string | undefined;
  const isTouched = touchedFields[name];
  const showError = showErrorOnTouched ? (isTouched || isSubmitted) && !!fieldError : !!fieldError;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <DepartmentSelector
          departments={departments}
          isLoading={isLoadingDepartments}
          value={field.value || ''}
          onChange={(value) => field.onChange(value || '')}
          isRequired={isRequired}
          isInvalid={showError}
          errorMessage={showError ? fieldError : undefined}
          placeholder={placeholder}
          label={label}
          showEmptyOption={showEmptyOption}
          isDisabled={isDisabled}
          {...rest}
        />
      )}
    />
  );
};

FormDepartmentSelector.displayName = 'FormDepartmentSelector';

export default FormDepartmentSelector;

